// Room Names (/volunteer/rooms).
//
// Every building on the campus map, every room number in it, and a box beside
// each for the name people actually use — "SSA Lounge" for Armes 209. Pick a
// building on the left, type names on the right (Enter saves and moves down),
// or search the whole campus from the bar at the top.
//
// ## Three layers, and what the box shows
//
//   sheet   the authored RoomNames/<slug>.csv (UMapsTools), deployed with the
//           map — what every client reads first;
//   live    what the operator has PUBLISHED from here (/api/room-names, KV)
//           — fetched by the website and the app on every load and laid over
//           the sheet by the mirrored `applyRoomNameOverlay`; wins over it;
//   draft   what a volunteer typed and nobody has published yet — seen only
//           on this page.
//
// The box shows draft ?? live ?? sheet; the pill beside it says which. A
// volunteer's save is a DRAFT; the operator's **Publish** moves drafts into
// the live set, and that is the whole publishing step: no download, no sync,
// no deploy — the website shows it within the edge cache's minute, the app on
// its next launch. **Unpublish** takes a name off again and the sheet's name
// stands. The "Export for UMapsTools" button is deliberately secondary: the
// authored sheets are the long-term home and fall behind the live set until
// somebody lands the export with `scripts/apply-room-names.py`, but nothing a
// student sees waits on that.
//
// ## The room list is the SHEET, not the map
//
// Rows come from the live `RoomNames/<slug>.csv` — the same rows the export
// writes back, so the round trip is lossless (Room Type survives, order
// survives). A building with no sheet yet gets the generator's own rule run
// client-side over its structure + nav files (`roomsFromFiles`), so nothing
// has to be generated before a volunteer can start. Stairwells and elevators
// are hidden by default; the pathfinder never offers them as destinations, so
// a name on one is wasted.
//
// Drafts are keyed `<slug>::<room>` and saved one room at a time: a volunteer
// working down a building saves two hundred small rows, and a whole-building
// save would make the first typo cost the lot. A name saved identical to what
// is already shown (live, else sheet) is not a draft — the row's draft is
// REMOVED instead, which is what "it was right after all" means.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ago } from './shared.js';
import {
  parseRoomSheet, serializeRoomSheet, isShaftRoom, roomsFromFiles, roomKey,
  applyDrafts, buildChanges, isPublished,
} from '../utils/roomSheets.js';
import { buildZip } from '../utils/zip.js';

const ROOT = '/Map%20Info/pathfinder/';
// Nav-only pseudo-buildings: a tunnel has no rooms to name.
const PSEUDO = new Set(['outdoors', 'tunnels', 'building_connections']);
const MAX_NAME = 80;
const MAX_RESULTS = 40;

// The SPA rewrite answers a missing file with index.html + 200 — the same
// trap `createHttpDataSource` documents — so an HTML body means "not there".
async function fetchFile(path, { text = false } = {}) {
  let res;
  try { res = await fetch(ROOT + path); } catch { return null; }
  if (!res.ok) return null;
  const ctype = String(res.headers?.get?.('content-type') || '').toLowerCase();
  if (ctype.includes('text/html')) return null;
  try { return text ? await res.text() : await res.json(); } catch { return null; }
}

async function loadBuildings() {
  const manifest = await fetchFile('manifest.json');
  if (!manifest) throw new Error('Could not load the campus building list.');
  const list = (manifest.buildings || []).filter((b) => b?.slug && !PSEUDO.has(b.slug));
  const out = await Promise.all(list.map(async ({ slug, name }) => {
    const sheet = await fetchFile(`RoomNames/${encodeURIComponent(slug)}.csv`, { text: true });
    if (sheet) return { slug, name: name || slug, rows: parseRoomSheet(sheet), sheet: true };
    const [struct, nav] = await Promise.all([
      fetchFile(`${encodeURIComponent(slug)}.json`), fetchFile(`nav_${encodeURIComponent(slug)}.json`),
    ]);
    return { slug, name: name || slug, rows: roomsFromFiles(struct, nav), sheet: false };
  }));
  return out.filter((b) => b.rows.length).sort((a, b) => a.name.localeCompare(b.name));
}

const stamp = () => new Date().toISOString().slice(0, 10);

export default function RoomNames({ token, onExpired, admin }) {
  const [buildings, setBuildings] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [live, setLive] = useState({});
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState('');
  const [q, setQ] = useState('');
  const [bq, setBq] = useState('');
  const [campusQ, setCampusQ] = useState('');
  const [showShafts, setShowShafts] = useState(false);
  const roomsRef = useRef(null);

  const loadDrafts = useCallback(async () => {
    const r = await api('/api/room-names?part=admin', { token, onExpired });
    if (!r.ok) { setErr(r.body.error || 'Could not load the saved names.'); return; }
    setDrafts(r.body.drafts || {});
    setLive(r.body.live || {});
  }, [token, onExpired]);

  useEffect(() => {
    let live = true;
    loadBuildings()
      .then((list) => { if (live) setBuildings(list); })
      .catch((e) => { if (live) setErr(e.message || 'Could not load the buildings.'); });
    loadDrafts();
    return () => { live = false; };
  }, [loadDrafts]);

  // Per-building tallies, for the list on the left.
  const tally = useMemo(() => {
    const t = {};
    for (const b of buildings || []) {
      let named = 0, pending = 0;
      for (const r of b.rows) {
        if (isShaftRoom(r.roomNumber)) continue;
        const id = roomKey(b.slug, r.roomNumber);
        const d = drafts[id], l = live[id];
        if (shownName(r, d, l)) named++;
        if (d) pending++;
      }
      t[b.slug] = { named, pending, total: b.rows.filter((r) => !isShaftRoom(r.roomNumber)).length };
    }
    return t;
  }, [buildings, drafts, live]);

  const building = useMemo(() => (buildings || []).find((b) => b.slug === selected) || null, [buildings, selected]);

  const buildingList = useMemo(() => {
    const needle = bq.trim().toLowerCase();
    return (buildings || []).filter((b) => !needle || b.name.toLowerCase().includes(needle) || b.slug.includes(needle));
  }, [buildings, bq]);

  const rows = useMemo(() => {
    if (!building) return [];
    const needle = q.trim().toLowerCase();
    return building.rows.filter((r) => {
      if (!showShafts && isShaftRoom(r.roomNumber)) return false;
      if (!needle) return true;
      const id = roomKey(building.slug, r.roomNumber);
      const name = shownName(r, drafts[id], live[id]).toLowerCase();
      return r.roomNumber.toLowerCase().includes(needle) || name.includes(needle);
    });
  }, [building, q, showShafts, drafts, live]);

  // The campus-wide search: every token has to land on the building, the room
  // number or the name (draft first), so "armes 209" and "lounge" both work.
  const campusHits = useMemo(() => {
    const tokens = campusQ.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length || !buildings) return [];
    const hits = [];
    for (const b of buildings) {
      const bname = b.name.toLowerCase();
      for (const r of b.rows) {
        if (isShaftRoom(r.roomNumber)) continue;
        const id = roomKey(b.slug, r.roomNumber);
        const d = drafts[id];
        const name = shownName(r, d, live[id]);
        const hay = [bname, r.roomNumber.toLowerCase(), name.toLowerCase(), (r.roomName || '').toLowerCase()];
        let score = 0, all = true;
        for (const t of tokens) {
          if (hay[1] === t) score += 100;
          else if (hay[1].startsWith(t)) score += 50;
          else if (hay[2].includes(t) || hay[3].includes(t)) score += 20;
          else if (hay[0].includes(t)) score += 10;
          else { all = false; break; }
        }
        if (all) hits.push({ b, r, name, draft: d || null, score });
        if (hits.length > 4000) break;
      }
    }
    return hits.sort((x, y) => y.score - x.score || x.b.name.localeCompare(y.b.name)).slice(0, MAX_RESULTS);
  }, [campusQ, buildings, drafts, live]);

  const jumpTo = (slug, roomNumber) => {
    setSelected(slug); setQ(roomNumber); setCampusQ('');
    setTimeout(() => roomsRef.current?.querySelector('input')?.focus(), 0);
  };

  const save = useCallback(async (slug, row, name) => {
    const id = roomKey(slug, row.roomNumber);
    const current = drafts[id] || null;
    // Typing back what is already shown (live, else the sheet) is "it was
    // right after all": no draft.
    if (name === baseName(row, live[id])) {
      if (!current) return { ok: true };
      const r = await api('/api/room-names', { method: 'POST', token, onExpired, body: { op: 'revert', building: slug, roomNumber: row.roomNumber } });
      if (!r.ok) return { ok: false, error: r.body.error || 'That did not save.' };
      setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      return { ok: true };
    }
    const r = await api('/api/room-names', {
      method: 'POST', token, onExpired,
      body: { op: 'save', building: slug, roomNumber: row.roomNumber, name, expectUpdatedAt: current?.updatedAt || '' },
    });
    if (r.status === 409) { await loadDrafts(); return { ok: false, error: r.body.error }; }
    if (!r.ok) return { ok: false, error: r.body.error || 'That did not save.' };
    setDrafts((d) => ({ ...d, [r.body.id]: r.body.draft }));
    return { ok: true };
  }, [drafts, live, token, onExpired, loadDrafts]);

  const revert = useCallback(async (slug, row) => {
    const id = roomKey(slug, row.roomNumber);
    const r = await api('/api/room-names', { method: 'POST', token, onExpired, body: { op: 'revert', building: slug, roomNumber: row.roomNumber } });
    if (!r.ok) return { ok: false, error: r.body.error || 'That did not go through.' };
    setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
    return { ok: true };
  }, [token, onExpired]);

  const pendingTotal = Object.values(tally).reduce((n, t) => n + t.pending, 0);

  return (
    <>
      <h1 className="vt-h1">Room Names</h1>
      <p className="vt-sub">
        Give room numbers the names people use. Names you save here are <b>drafts</b> until UMaps publishes them —
        then they show on the website within a minute and in the app on its next launch.
      </p>
      {err && <div className="vt-err">{err}</div>}
      {admin && buildings && <AdminPanel buildings={buildings} drafts={drafts} live={live} token={token} onExpired={onExpired} reload={loadDrafts} />}

      <div className="vt-toolbar">
        <input className="vt-input" style={{ maxWidth: 520 }} placeholder="Search the whole campus — a room number, a name, “armes 209”, “lounge”"
               value={campusQ} onChange={(e) => setCampusQ(e.target.value)} aria-label="Search every building" />
        {!!pendingTotal && <span className="vt-pill vt-pill-warn">{pendingTotal} waiting to publish</span>}
      </div>
      {campusQ.trim() && (
        <div className="vt-results" role="list">
          {campusHits.map(({ b, r, name, draft }) => (
            <button key={`${b.slug}:${r.roomNumber}`} className="vt-sideitem" onClick={() => jumpTo(b.slug, r.roomNumber)} role="listitem">
              <span style={{ fontWeight: 650 }}>{b.name} {r.roomNumber}</span>
              {name && <span className="vt-meta" style={{ marginTop: 0, display: 'inline', marginLeft: 8 }}>{name}</span>}
              {draft && <span className="vt-pill vt-pill-warn" style={{ marginLeft: 8 }}>Draft</span>}
            </button>
          ))}
          {!campusHits.length && <div className="vt-meta" style={{ padding: 12 }}>Nothing matches across campus.</div>}
        </div>
      )}

      {!buildings ? <div className="vt-empty">Loading every building’s rooms…</div> : (
        <div className="vt-split">
          <div className="vt-card vt-side">
            <div style={{ padding: 10 }}>
              <input className="vt-input" placeholder="Find a building" value={bq} onChange={(e) => setBq(e.target.value)} aria-label="Find a building" />
            </div>
            <div className="vt-sidelist">
              {buildingList.map((b) => {
                const t = tally[b.slug];
                return (
                  <button key={b.slug} className="vt-sideitem" aria-current={b.slug === selected} onClick={() => { setSelected(b.slug); setQ(''); }}>
                    <div style={{ fontWeight: 650, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="vt-grow">{b.name}</span>
                      {!!t.pending && <span className="vt-pill vt-pill-warn">{t.pending}</span>}
                    </div>
                    <div className="vt-meta" style={{ marginTop: 0 }}>{t.named} of {t.total} named{b.sheet ? '' : ' · no sheet yet'}</div>
                  </button>
                );
              })}
              {!buildingList.length && <div className="vt-meta" style={{ padding: 12 }}>No building by that name.</div>}
            </div>
          </div>

          <div ref={roomsRef}>
            {!building ? (
              <div className="vt-empty">Pick a building on the left, or search the campus above.</div>
            ) : (
              <div className="vt-card vt-pad">
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap' }}>
                  <div className="vt-grow">
                    <div className="vt-title" style={{ fontSize: 18 }}>{building.name}</div>
                    <div className="vt-meta">
                      {tally[building.slug].named} of {tally[building.slug].total} rooms have a name
                      {tally[building.slug].pending ? ` · ${tally[building.slug].pending} waiting to publish` : ''}
                      {!building.sheet && ' · this building has no sheet yet, so its rooms are read straight off the map'}
                    </div>
                  </div>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--dim)' }}>
                    <input type="checkbox" checked={showShafts} onChange={(e) => setShowShafts(e.target.checked)} /> Show stairs &amp; elevators
                  </label>
                </div>
                <div className="vt-toolbar">
                  <input className="vt-input" placeholder={`Find a room in ${building.name}`} value={q} onChange={(e) => setQ(e.target.value)}
                         aria-label={`Find a room in ${building.name}`} />
                  {q && <button className="vt-btn vt-small" onClick={() => setQ('')}>Clear</button>}
                </div>
                <table className="vt-rooms">
                  <thead>
                    <tr><th>Room</th><th>Name</th><th></th></tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <RoomRow key={`${building.slug}:${r.roomNumber}`} slug={building.slug} row={r}
                               draft={drafts[roomKey(building.slug, r.roomNumber)] || null}
                               live={live[roomKey(building.slug, r.roomNumber)] || null}
                               onSave={save} onRevert={revert} />
                    ))}
                    {!rows.length && <tr><td colSpan={3} className="vt-meta" style={{ padding: 14 }}>No room matches.</td></tr>}
                  </tbody>
                </table>
                <div className="vt-hint" style={{ marginTop: 10 }}>
                  Enter saves and moves to the next room; Esc puts back what was there. Leave a box blank to say the room has no name.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * One room. The box holds the draft if there is one, else the sheet's name;
 * it saves itself on Enter or on leaving the box with a change, so a volunteer
 * never has to find a Save button two hundred rows down.
 *
 * ⚠️ Enter moves focus BEFORE the save returns, and moving focus blurs this
 * box, and blur also saves. Without `inflight` the blur would run with the
 * closure from before the save landed — `shown` still the old name — and post
 * the same name a second time, which the server then refuses as a conflict
 * (its `updatedAt` moved) and the row shows an error for a save that worked.
 * The ref is the one place that knows a value is already on its way.
 */
function RoomRow({ slug, row, draft, live, onSave, onRevert }) {
  const shown = shownName(row, draft, live);
  const base = baseName(row, live);
  const [value, setValue] = useState(shown);
  const [busy, setBusy] = useState(false);
  const [rowErr, setRowErr] = useState('');
  const [flash, setFlash] = useState(false);
  const inputRef = useRef(null);
  const inflight = useRef(null);

  // A draft arriving from elsewhere (a reload after a conflict, or the save
  // itself landing) resets the box — and ends the save's claim on it.
  useEffect(() => { setValue(shown); inflight.current = null; }, [shown]);

  const pending = !!draft;

  const commit = async (advance) => {
    const next = value.replace(/\s+/g, ' ').trim();
    if (next !== value) setValue(next);
    if (next === shown || next === inflight.current) { if (advance) focusNext(inputRef.current); return; }
    inflight.current = next;
    if (advance) focusNext(inputRef.current); // move on at once; the save follows
    setBusy(true); setRowErr('');
    const out = await onSave(slug, row, next);
    setBusy(false);
    if (!out.ok) { inflight.current = null; setRowErr(out.error || 'That did not save.'); return; }
    setFlash(true); setTimeout(() => setFlash(false), 1200);
  };

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(true); }
    else if (e.key === 'Escape') { e.preventDefault(); setValue(shown); setRowErr(''); }
  };

  const revert = async () => {
    setBusy(true); setRowErr('');
    const out = await onRevert(slug, row);
    setBusy(false);
    if (!out.ok) setRowErr(out.error || 'That did not go through.');
  };

  return (
    <tr className={pending ? 'vt-draft' : undefined}>
      <td className="vt-roomno">{row.roomNumber}</td>
      <td>
        <input ref={inputRef} className="vt-input" value={value} maxLength={MAX_NAME}
               aria-label={`Name for room ${row.roomNumber}`} placeholder={row.roomType ? row.roomType.toLowerCase() : ''}
               onChange={(e) => setValue(e.target.value)} onKeyDown={onKey} onBlur={() => commit(false)} />
        {rowErr && <div className="vt-hint" style={{ color: 'var(--bad)' }}>{rowErr}</div>}
        {pending && base && base !== draft.name && <div className="vt-roomwas">was “{base}”</div>}
      </td>
      <td className="vt-roomstate">
        {flash ? <span className="vt-pill vt-pill-good">Saved</span>
          : pending ? <span className="vt-pill vt-pill-warn" title={`${draft.by || 'someone'} · ${ago(draft.updatedAt)}`}>Draft</span>
          : live ? <span className="vt-pill vt-pill-good" title={`Published ${ago(live.publishedAt)}${live.name ? '' : ' — the sheet name is hidden'}`}>Live</span>
          : null}
        {draft && <button className="vt-btn vt-ghost vt-small" style={{ marginLeft: 6 }} disabled={busy} onClick={revert}
                          title="Forget this draft and keep what the sheet says">Reset</button>}
      </td>
    </tr>
  );
}

/** What a student sees for this room: the published name, else the sheet's. */
function baseName(row, live) {
  return live ? (live.name || '') : (row.roomName || '');
}
/** What this page shows: a draft outranks both. */
function shownName(row, draft, live) {
  return draft ? draft.name : baseName(row, live);
}

function focusNext(input) {
  const tr = input?.closest('tr');
  const next = tr?.nextElementSibling?.querySelector('input');
  if (next) { next.focus(); next.select?.(); }
}

/**
 * The operator's half: the drafts waiting, Publish (the whole step), the live
 * set with Unpublish, and — secondary — the export that lets the authored
 * sheets in UMapsTools catch up.
 */
function AdminPanel({ buildings, drafts, live, token, onExpired, reload }) {
  const [open, setOpen] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const pending = useMemo(() => buildChanges(buildings, drafts), [buildings, drafts]);
  const published = useMemo(() => buildChanges(buildings, live), [buildings, live]);
  const touched = new Set(published.map((c) => c.building));

  const post = async (body, label) => {
    setBusy(label); setMsg(''); setErr('');
    const r = await api('/api/room-names', { method: 'POST', token, onExpired, body });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not go through.'); return null; }
    await reload();
    return r.body;
  };
  const publish = async (ids) => {
    const n = ids ? ids.length : pending.length;
    if (!n) return;
    if (!window.confirm(`Publish ${n} name${n === 1 ? '' : 's'}? They show on the website within a minute and in the app on its next launch.`)) return;
    const out = await post({ op: 'publish', ...(ids ? { ids } : {}) }, 'publish');
    if (out) setMsg(`Published ${out.published}.`);
  };
  const unpublish = async (ids) => {
    const out = await post({ op: 'unpublish', ids }, `un:${ids.join()}`);
    if (out) setMsg(`Took ${out.removed} off the map; the sheet name stands.`);
  };
  const drop = (c) => post({ op: 'revert', building: c.building, roomNumber: c.roomNumber }, `rv:${c.building}:${c.roomNumber}`);

  // The live set as drop-in sheets, so UMapsTools can be brought up to date.
  const exportSheets = () => {
    const files = [];
    for (const b of buildings) {
      if (!touched.has(b.slug)) continue;
      files.push({ path: `RoomNames/${b.slug}.csv`, data: serializeRoomSheet(applyDrafts(b.slug, b.rows, live)) });
    }
    files.push({ path: 'changes.json', data: JSON.stringify({ exportedAt: new Date().toISOString(), changes: published.filter((c) => !isPublished(c)) }, null, 2) + '\n' });
    files.push({ path: 'README.txt', data: [
      'UMaps room names — the PUBLISHED set from myumaps.ca/volunteer/rooms',
      '',
      'These names are already live on the website and in the app (served from',
      '/api/room-names). This export exists so the authored sheets in UMapsTools',
      'catch up with them:',
      '',
      'RoomNames/<slug>.csv  each touched building\'s sheet with the live names laid in',
      'changes.json          only the rooms whose live name differs from the sheet',
      '',
      '  python3 scripts/apply-room-names.py <this zip>            # dry run',
      '  python3 scripts/apply-room-names.py <this zip> --apply',
      '  python3 scripts/sync-to-umapsweb.py pathfinder --apply',
      '',
    ].join('\n') });
    const blob = new Blob([buildZip(files)], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `umaps-room-names-${stamp()}.zip`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const Table = ({ rows, kind }) => (
    <table className="vt-rooms" style={{ marginTop: 12 }}>
      <thead><tr><th>Building</th><th>Room</th><th>Name</th><th>Sheet says</th><th>By</th><th></th></tr></thead>
      <tbody>
        {rows.map((c) => {
          const id = roomKey(c.building, c.roomNumber);
          return (
            <tr key={id} className={kind === 'draft' ? 'vt-draft' : undefined}>
              <td>{c.buildingName}</td>
              <td className="vt-roomno">{c.roomNumber}{c.unknownRoom && <span className="vt-pill vt-pill-bad" style={{ marginLeft: 6 }}>not on sheet</span>}</td>
              <td>{c.name || <i style={{ color: 'var(--faint)' }}>(blank — hides the sheet name)</i>}</td>
              <td className="vt-roomwas">{c.previous || '—'}</td>
              <td className="vt-meta" style={{ marginTop: 0 }}>{c.by || '—'} · {ago(c.updatedAt)}</td>
              <td className="vt-roomstate">
                {kind === 'draft' ? (
                  <>
                    <button className="vt-btn vt-small vt-primary" disabled={!!busy} onClick={() => publish([id])}>Publish</button>
                    <button className="vt-btn vt-ghost vt-small" style={{ marginLeft: 4 }} disabled={!!busy} onClick={() => drop(c)}>Drop</button>
                  </>
                ) : (
                  <>
                    {isPublished(c) && <span className="vt-pill vt-pill-dim" title="The authored sheet already carries this name">In sheet</span>}
                    <button className="vt-btn vt-ghost vt-small" style={{ marginLeft: 4 }} disabled={!!busy} onClick={() => unpublish([id])}>Unpublish</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
        {!rows.length && <tr><td colSpan={6} className="vt-meta" style={{ padding: 12 }}>Nothing here.</td></tr>}
      </tbody>
    </table>
  );

  return (
    <div className="vt-card vt-pad" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="vt-stats">
          <div><b>{pending.length}</b> draft{pending.length === 1 ? '' : 's'} waiting</div>
          <div><b>{published.length}</b> published from here</div>
          <div><b>{touched.size}</b> building{touched.size === 1 ? '' : 's'} with live names</div>
        </div>
        <div className="vt-actions" style={{ marginLeft: 'auto' }}>
          <button className="vt-btn" aria-pressed={open === 'draft'} onClick={() => setOpen((o) => (o === 'draft' ? '' : 'draft'))} disabled={!pending.length}>Review drafts</button>
          <button className="vt-btn" aria-pressed={open === 'live'} onClick={() => setOpen((o) => (o === 'live' ? '' : 'live'))} disabled={!published.length}>Live names</button>
          <button className="vt-btn vt-ghost" onClick={exportSheets} disabled={!published.length} title="A zip of the touched sheets, for UMapsTools' apply-room-names.py">Export for UMapsTools</button>
          <button className="vt-btn vt-primary" onClick={() => publish()} disabled={!pending.length || busy === 'publish'}>Publish all drafts</button>
        </div>
      </div>
      <div className="vt-hint" style={{ marginTop: 8 }}>
        Publishing puts the names on the website and in the app — no deploy. The export is only for bringing the authored sheets in UMapsTools up to date.
      </div>
      {msg && <div className="vt-ok" style={{ marginTop: 10 }}>{msg}</div>}
      {err && <div className="vt-err" style={{ marginTop: 10 }}>{err}</div>}
      {open === 'draft' && <Table rows={pending} kind="draft" />}
      {open === 'live' && <Table rows={published} kind="live" />}
    </div>
  );
}
